import axios from "axios";
import { normalizePaymentStatus } from '../utils/paymentStatus';

let authBearer = null;

// Base API URL (siempre con slash final)
const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_BASE || "/v1/").trim();
  return raw.endsWith("/") ? raw : `${raw}/`;
})();

export function setAuthTokens({ access, refresh }) {
  try {
    if (access) localStorage.setItem("access_token", access);
    if (refresh) localStorage.setItem("refresh_token", refresh);
  } catch {}

  authBearer = access ? `Bearer ${access}` : null;

  if (authBearer) {
    API.defaults.headers.common["Authorization"] = authBearer;
  } else {
    try {
      delete API.defaults.headers.common["Authorization"];
    } catch {}
  }
}

export function clearAuthTokens() {
  try {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
  } catch {}

  authBearer = null;
  try {
    delete API.defaults.headers.common["Authorization"];
  } catch {}
}

const API = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

API.interceptors.request.use((config) => {
  const t = localStorage.getItem("access_token");
  if (t) config.headers.Authorization = `Bearer ${t}`;
  return config;
});

API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status ?? null;

    if (status === 401) {
      const refreshToken = localStorage.getItem("refresh_token");

      // Evita loops: si ya estamos refrescando y falló, no reintentar infinito
      const originalRequest = error?.config;
      if (!refreshToken || originalRequest?._retry) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        const res = await axios.post(`${API_BASE}auth/token/refresh/`, {
          refresh: refreshToken,
        });

        localStorage.setItem("access_token", res.data.access);

        // Actualiza header para siguientes requests
        API.defaults.headers.common["Authorization"] = `Bearer ${res.data.access}`;

        // Reintenta la petición original usando la misma instancia
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${res.data.access}`;

        return API.request(originalRequest);
      } catch (err) {
        console.error("Error al refrescar el token", err);
        try {
          sessionStorage.clear();
        } catch {}
        try {
          clearAuthTokens();
        } catch {}

        try {
          const bc = new BroadcastChannel("auth");
          bc.postMessage("logout");
          bc.close();
        } catch {}

        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

export default API;

const withDebtFallback = async (primary, fallback) => {
  try {
    return await primary();
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404) return await fallback();
    throw err;
  }
};

// Endpoints específicos de liquidación
export const SettlementsAPI = {
  settle: (payload) => API.post("payment/settle/", payload),
  preview: (payload) => API.post("payment/invoices/preview/", payload),
  eligible: (supplierId, options = {}) => {
    const scope = options.scope || "payable";
    const limit = Number(options.limit || 0);
    const offset = Number(options.offset || 0);
    const params = {
      supplier_id: supplierId,
      status: "ICF",
      scope,
    };
    if (Array.isArray(options.payment_status) && options.payment_status.length > 0) {
      const paymentStatuses = [...new Set(
        options.payment_status
          .map((status) => normalizePaymentStatus(status, null))
          .filter(Boolean)
      )];
      if (paymentStatuses.length > 0) {
        params["payment_status[]"] = paymentStatuses;
      }
    }
    if (Number.isFinite(limit) && limit > 0) params.limit = limit;
    if (Number.isFinite(offset) && offset > 0) params.offset = offset;
    return API.get("payment/invoices/eligible/", { params });
  },
  applications: (invoiceId) => API.get(`payment/invoices/${invoiceId}/applications/`),
  compensateAdjustments: (payload) => API.post("payment/invoices/compensate-adjustments/", payload),
  bills: (supplierOrParams) => {
    const params =
      supplierOrParams && typeof supplierOrParams === "object"
        ? supplierOrParams
        : supplierOrParams
          ? { supplier_id: supplierOrParams }
          : {};
    return API.get("payment/bills/", { params });
  },
  payBill: (id, payload) => API.patch(`payment/bills/${id}/pay/`, payload),
  checkRef: (number) =>
    API.get("payment/payment-documents/check-reference/", { params: { number } }),
  checkCreditNote: (number) =>
    API.get("payment/credit-notes/check-number/", { params: { number } }),
  checkReceipt: (number) =>
    API.get("payment/receipts/check-number/", { params: { number } }),
  checkReceiveNote: (number) =>
    API.get("payment/receive-notes/check/", { params: { number } }),
  paymentsList: (params) => API.get("payment/payments/", { params }),
  paymentDetail: (id) => API.get(`payment/payments/${id}/`),
  updatePayment: (id, payload) => API.patch(`payment/payments/${id}/`, payload),
  correctPaymentDate: (id, payment_date, correction_reason) => API.patch(
    `payment/payments/${id}/`,
    { payment_date, correction_reason }
  ),
  addReceipt: (paymentId, payload) => API.post(`payment/payments/${paymentId}/receipts/`, payload),
  cancelPayment: (id) => API.post(`payment/payments/${id}/cancel/`),
  destroyPayment: (id) => API.delete(`payment/payments/${id}/destroy/`),

  listDebts: (params) =>
    withDebtFallback(
      () => API.get("payment/debts/", { params }),
      () => API.get("debts/", { params })
    ),
  createDebt: (payload) =>
    withDebtFallback(
      () => API.post("payment/debts/", payload),
      () => API.post("debts/", payload)
    ),
  debtDetail: (id) =>
    withDebtFallback(
      () => API.get(`payment/debts/${id}/`),
      () => API.get(`debts/${id}/`)
    ),
  addDebtPayment: (id, payload) =>
    withDebtFallback(
      () => API.post(`payment/debts/${id}/add_payment/`, payload),
      () => API.post(`debts/${id}/add_payment/`, payload)
    ),
};

export const PaymentCrud = {
  receipts: {
    list: (params) => API.get("payment/receipts/", { params }),
    create: (payload) => API.post("payment/receipts/", payload),
    update: (id, payload) => API.patch(`payment/receipts/${id}/`, payload),
    remove: (id) => API.delete(`payment/receipts/${id}/`),
  },
  creditNotes: {
    list: (params) => API.get("payment/credit-notes/", { params }),
    create: (payload) => API.post("payment/credit-notes/", payload),
    update: (id, payload) => API.patch(`payment/credit-notes/${id}/`, payload),
    remove: (id) => API.delete(`payment/credit-notes/${id}/`),
  },
};

// Validación genérica de unicidad
export const ValidateAPI = {
  checkUnique: (params) => API.get("payment/validate/uniqueness/", { params }),
};

export const CompanyAPI = {
  myCompanies: () => API.get("company/company/my_companies/"),
  listRoles: (company_id) => API.get("company/roles/by_company", { params: { company_id } }),
  permissions: (params) => API.get("company/permissions/", { params }),
  switchCompany: (company_id) => API.post("company/access/switch_company/", { company_id }),
  assignUserCompany: (payload) => API.post("company/user-company/assign/", payload),
  companiesForUser: (user_id = "me") =>
    API.get("company/user-company/companies_for_user/", { params: { user_id } }),
};

// RBAC helper group
export const RBAC = {
  companiesMine: () => API.get("company/companies/mine/"),
  permissions: () => API.get("company/permissions/"),
  createPermission: (payload) => API.post("company/permissions/", payload),
  updatePermission: (id, payload) => API.put(`company/permissions/${id}/`, payload),
  deletePermission: (id) => API.delete(`company/permissions/${id}/`),

  rolesByCompany: (companyId) =>
    API.get("company/roles/by_company/", { params: { company_id: companyId } }),
  rolesList: (params) => API.get("company/roles/", { params }),
  createRole: (payload) => API.post("company/roles/", payload),
  updateRole: (id, payload) => API.patch(`company/roles/${id}/`, payload),
  deleteRole: (id) => API.delete(`company/roles/${id}/`),

  userCompanies: (userId) =>
    API.get("company/user-company/companies_for_user/", {
      params: { user_id: userId || "me" },
    }),
  assignUserCompany: (payload) => API.post("company/user-company/assign/", payload),

  switchCompany: (companyId) => API.post("company/access/switch_company/", { company_id: companyId }),

  companies: {
    list: (params) => API.get("company/companies/", { params }),
    create: (payload) => API.post("company/companies/", payload),
    update: (id, payload) => API.patch(`company/companies/${id}/`, payload),
    remove: (id) => API.delete(`company/companies/${id}/`),
  },

  warehouses: {
    list: (params) => API.get("company/warehouses/", { params }),
    create: (payload) => API.post("company/warehouses/", payload),
    update: (id, payload) => API.patch(`company/warehouses/${id}/`, payload),
    remove: (id) => API.delete(`company/warehouses/${id}/`),
  },
  logicalWarehouses: {
    list: (params) => API.get("company/logical-warehouses/", { params }),
    create: (payload) => API.post("company/logical-warehouses/", payload),
    update: (id, payload) => API.patch(`company/logical-warehouses/${id}/`, payload),
    remove: (id) => API.delete(`company/logical-warehouses/${id}/`),
  },
  receiveNotes: {
    list: (params) => API.get("receive-note/", { params }),
    create: (payload) => API.post("receive-note/", payload),
    update: (id, payload) => API.patch(`receive-note/${id}/`, payload),
    remove: (id) => API.delete(`receive-note/${id}/`),
    toggleActive: (id, is_active) => API.patch(`receive-note/${id}/toggle_active/`, { is_active }),
  },
  categories: {
    list: (params) => API.get("catalogs/categories/", { params }),
    create: (payload) => API.post("catalogs/categories/", payload),
    update: (id, payload) => API.patch(`catalogs/categories/${id}/`, payload),
    remove: (id) => API.delete(`catalogs/categories/${id}/`),
  },
  priceTypes: {
    list: (params) => API.get("catalogs/price-types/", { params }),
    create: (payload) => API.post("catalogs/price-types/", payload),
    update: (id, payload) => API.patch(`catalogs/price-types/${id}/`, payload),
    remove: (id) => API.delete(`catalogs/price-types/${id}/`),
  },
  objectiveTypes: {
    list: (params) => API.get("catalogs/objective-types/", { params }),
    create: (payload) => API.post("catalogs/objective-types/", payload),
    update: (id, payload) => API.patch(`catalogs/objective-types/${id}/`, payload),
    remove: (id) => API.delete(`catalogs/objective-types/${id}/`),
  },
  formats: {
    list: (params) => API.get("invoice/catalogs/price-formats/", { params }),
    create: (payload) => API.post("invoice/catalogs/price-formats/", payload),
    update: (id, payload) => API.patch(`invoice/catalogs/price-formats/${id}/`, payload),
    remove: (id) => API.delete(`invoice/catalogs/price-formats/${id}/`),
  },
  invoiceTypes: {
    list: (params) => API.get("invoice/catalogs/invoice-types/", { params }),
    create: (payload) => API.post("invoice/catalogs/invoice-types/", payload),
    update: (id, payload) => API.patch(`invoice/catalogs/invoice-types/${id}/`, payload),
    remove: (id) => API.delete(`invoice/catalogs/invoice-types/${id}/`),
  },
  orderCatalogs: {
    create: () => API.get("catalogs/create_order/"),
    product: () => API.get("catalogs/create_product/"),
  },
};

// Accounts helpers
export const Accounts = {
  users: (params) => API.get("accounts/users/", { params }),
  setUserActive: (id, isActive) =>
    API.patch(`accounts/users/${id}/set_active/`, { is_active: isActive }),
};

export const SellOutAPI = {
  dashboard: (params) => API.get("sellout/dashboard/", { params }),
  missing: (params) => API.get("sellout/missing/", { params }),
  exportLegacy: (params) => API.get("sellout/export-legacy/", { params }),
  commitments: {
    list: (params) => API.get("sellout/commitments/", { params }),
    create: (payload) => API.post("sellout/commitments/", payload),
    update: (id, payload) => API.patch(`sellout/commitments/${id}/`, payload),
    remove: (id) => API.delete(`sellout/commitments/${id}/`),
    detail: (id) => API.get(`sellout/commitments/${id}/detail/`),
    summary: (id) => API.get(`sellout/commitments/${id}/summary/`),
    byOrder: (params) => API.get("sellout/commitments/by-order/", { params }),
    summaryByCommitment: (params) => API.get("sellout/commitments/summary-by-commitment/", { params }),
  },
  commitmentLines: {
    list: (params) => API.get("sellout/commitment-lines/", { params }),
    create: (payload) => API.post("sellout/commitment-lines/", payload),
    update: (id, payload) => API.patch(`sellout/commitment-lines/${id}/`, payload),
    remove: (id) => API.delete(`sellout/commitment-lines/${id}/`),
    pendingBySupplier: (params) => API.get("sellout/commitment-lines/pending-by-supplier/", { params }),
    missing: (params) => API.get("sellout/commitment-lines/missing/", { params }),
    summaryByOrder: (params) => API.get("sellout/commitment-lines/summary-by-order/", { params }),
    summaryBySupplier: (params) => API.get("sellout/commitment-lines/summary-by-supplier/", { params }),
  },
  documents: {
    list: (params) => API.get("sellout/documents/", { params }),
    create: (payload) => API.post("sellout/documents/", payload),
    update: (id, payload) => API.patch(`sellout/documents/${id}/`, payload),
    remove: (id) => API.delete(`sellout/documents/${id}/`),
    previewAllocation: (payload) => API.post("sellout/documents/preview-allocation/", payload),
  },
  documentLines: {
    list: (params) => API.get("sellout/document-lines/", { params }),
    create: (payload) => API.post("sellout/document-lines/", payload),
    update: (id, payload) => API.patch(`sellout/document-lines/${id}/`, payload),
    remove: (id) => API.delete(`sellout/document-lines/${id}/`),
  },
};
